import { makeAutoObservable, runInAction } from "mobx";
import {
  builtInSystemProfile,
  freezeWorkspaceProfile,
  type LogicalScreenSlot,
  type WorkspacePanel,
  type WorkspaceProfile,
} from "./workspace-model";
import type { WorkspacePreferencePort } from "./workspace-preference-port";
import { unavailableWorkspacePreferencePort } from "./workspace-preference-port";
import {
  fromFlexLayoutJson,
  sanitizeWorkspaceProfile,
  type WorkspaceDiagnostic,
  type WorkspaceViewPolicyResolver,
} from "./flexlayout-workspace-adapter";

export type WorkspaceNotice =
  | "fewer-monitors-fallback"
  | "layout-load-failed"
  | "layout-save-failed"
  | "popup-blocked"
  | null;

type WindowOpener = (url: string, target: string, features: string) => Window | null;

export class WorkspaceStore {
  profiles: readonly WorkspaceProfile[] = [builtInSystemProfile];
  activeProfileId = builtInSystemProfile.id;
  availableSlots = new Set<LogicalScreenSlot>(["PRIMARY"]);
  notice: WorkspaceNotice = null;
  loading = false;
  saving = false;
  diagnostics: readonly WorkspaceDiagnostic[] = [];

  constructor(
    private readonly preferences: WorkspacePreferencePort = unavailableWorkspacePreferencePort,
    private readonly resolveViewPolicy: WorkspaceViewPolicyResolver = (viewId) =>
      viewId === "shell_status"
        ? { allowClose: false, allowDetach: false, allowMove: false, allowMultiple: false, minHeight: 240, minWidth: 320 }
        : null,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get activeProfile(): WorkspaceProfile {
    return this.profiles.find((profile) => profile.id === this.activeProfileId) ?? builtInSystemProfile;
  }

  get panels(): readonly WorkspacePanel[] { return this.activeProfile.layout.panels; }
  get canSaveActiveProfile(): boolean { return this.activeProfile.level === "user" && this.activeProfile.locked !== true; }

  viewPolicy(viewId: string) {
    return this.resolveViewPolicy(viewId);
  }

  visibleSlot(panel: WorkspacePanel): LogicalScreenSlot {
    return this.availableSlots.has(panel.logicalScreen) ? panel.logicalScreen : "PRIMARY";
  }

  async load(): Promise<void> {
    this.loading = true;
    try {
      const loaded = await this.preferences.loadAvailableProfiles();
      runInAction(() => {
        const sanitized = loaded.map((profile) => sanitizeWorkspaceProfile(profile, this.resolveViewPolicy));
        this.diagnostics = Object.freeze(sanitized.flatMap((result) => result.diagnostics));
        this.profiles = sanitized.length === 0
          ? [builtInSystemProfile]
          : sanitized.map((result) => result.profile);
        if (!this.profiles.some((profile) => profile.id === this.activeProfileId)) {
          this.activeProfileId = this.profiles[0]?.id ?? builtInSystemProfile.id;
        }
        this.notice = this.panels.some((panel) => !this.availableSlots.has(panel.logicalScreen))
          ? "fewer-monitors-fallback"
          : null;
      });
    } catch {
      runInAction(() => {
        this.profiles = [builtInSystemProfile];
        this.activeProfileId = builtInSystemProfile.id;
        this.notice = "layout-load-failed";
      });
    } finally {
      runInAction(() => { this.loading = false; });
    }
  }

  applyFlexLayoutJson(json: unknown): boolean {
    if (this.activeProfile.locked === true) return false;
    try {
      const result = fromFlexLayoutJson(json, this.activeProfile, this.resolveViewPolicy);
      this.diagnostics = result.diagnostics;
      this.profiles = this.profiles.map((profile) =>
        profile.id === result.profile.id ? result.profile : profile,
      );
      return true;
    } catch {
      this.notice = "layout-load-failed";
      return false;
    }
  }

  reconcileAccess(): void {
    const sanitized = this.profiles.map((profile) =>
      sanitizeWorkspaceProfile(profile, this.resolveViewPolicy),
    );
    this.profiles = sanitized.map((result) => result.profile);
    this.diagnostics = Object.freeze(sanitized.flatMap((result) => result.diagnostics));
  }

  selectProfile(profileId: string): void {
    if (this.profiles.some((profile) => profile.id === profileId)) {
      this.activeProfileId = profileId;
      this.notice = this.panels.some((panel) => !this.availableSlots.has(panel.logicalScreen))
        ? "fewer-monitors-fallback"
        : null;
    }
  }

  assignSlot(panelId: string, slot: LogicalScreenSlot): void {
    this.replacePanel(panelId, (panel) => ({ ...panel, logicalScreen: slot }));
    if (!this.availableSlots.has(slot)) this.notice = "fewer-monitors-fallback";
  }

  setAvailableSlots(slots: readonly LogicalScreenSlot[]): void {
    this.availableSlots = new Set<LogicalScreenSlot>(["PRIMARY", ...slots]);
    this.notice = this.panels.some((panel) => !this.availableSlots.has(panel.logicalScreen))
      ? "fewer-monitors-fallback"
      : null;
  }

  async saveActiveProfile(): Promise<boolean> {
    const profile = this.activeProfile;
    if (!this.canSaveActiveProfile) {
      this.notice = "layout-save-failed";
      return false;
    }
    this.saving = true;
    try {
      const saved = await this.preferences.saveOwnProfile(profile, profile.version);
      runInAction(() => {
        this.profiles = this.profiles.map((item) => item.id === saved.id ? saved : item);
        this.notice = null;
      });
      return true;
    } catch {
      runInAction(() => { this.notice = "layout-save-failed"; });
      return false;
    } finally {
      runInAction(() => { this.saving = false; });
    }
  }

  popOut(
    panelId: string,
    slot: Exclude<LogicalScreenSlot, "PRIMARY">,
    opener: WindowOpener = (url, target, features) => window.open(url, target, features),
  ): boolean {
    const opened = opener(
      `/workspace/popout/${encodeURIComponent(panelId)}?slot=${encodeURIComponent(slot)}`,
      `thw-${slot.toLowerCase()}`,
      "popup=yes,width=1100,height=760",
    );
    if (opened === null) {
      this.notice = "popup-blocked";
      return false;
    }
    this.availableSlots.add(slot);
    this.replacePanel(panelId, (panel) => ({
      ...panel,
      logicalScreen: slot,
      poppedOut: true,
    }));
    this.notice = null;
    return true;
  }

  get windowPlacementEnhancementAvailable(): boolean {
    return typeof window !== "undefined" && "getScreenDetails" in window;
  }

  private replacePanel(
    panelId: string,
    update: (panel: WorkspacePanel) => WorkspacePanel,
  ): void {
    const active = this.activeProfile;
    const next = freezeWorkspaceProfile({
      ...active,
      layout: {
        ...active.layout,
        panels: active.layout.panels.map((panel) => panel.id === panelId ? update(panel) : panel),
      },
    });
    this.profiles = this.profiles.map((profile) => profile.id === active.id ? next : profile);
  }
}
